import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from './store';
import { Fact, DimensionReference } from './AfrSlice';
import { Concept, DimensionalConceptReference, ConceptRef, Label } from './ConceptSlice';
import { ConceptNode } from './AccountsSlice'
import http from '../utils/http';
import { Afr } from './AfrSlice';

export interface AccountDependency {
    target: Concept | null;
    dependencies: AccountDependency[];
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    name: string | null;
    dimensionsToMatch: DimensionReference[] | null;
    dimensionQualifiers: DimensionReference[] | null;
    dimensionDisQualifiers: DimensionReference[] | null;
    decimals: number | null;
}
export interface AccountRootDependency {
    ruleId: string | null;
    target: Concept | null;
    preconditions: AccountDependency[] | null;
    dependencies: AccountDependency[];
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    message: string | null;
    name: string | null;
    dimensionsToMatch: DimensionReference[] | null;
    dimensionQualifiers: DimensionReference[] | null;
    dimensionDisQualifiers: DimensionReference[] | null;
    year: number | null;
    deprecationDate: string | null;
    decimals: number | null;
}
export interface DtsConcept {
    parent: DtsConcept | null;
    concept: Concept | null;
    year: number;
    entityScope: Concept[] | null;
    dimensionalBreakdowns: ConceptNode[] | null;
    dimensionalScope: DimensionReference[] | null;
    dependencies: AccountRootDependency[] | null;
    new: boolean | null
}

export interface DtsConceptState {
    selectedConcept: DtsConcept | null;
    selectedReplacementConcept: DtsConcept | null;
    status: 'idle' | 'loading' | 'failed';
}


const initialState: DtsConceptState = {
    selectedConcept: null,
    selectedReplacementConcept: null,
    status: "idle"
}

export const isNewConcept = function (c: DtsConcept) {
    if (c.hasOwnProperty("new")) {
        return c.new === true;
    }
    return false;
}

export const getDeprecationDate = function (c: Concept): string | null {
    if (!c) {
        return null;
    }
    var depDateLabel: Label | undefined = c.labels.find(l => l.roleName === "deprecatedDateLabel");
    if (depDateLabel) {
        return depDateLabel.value;
    }
    return null;
}

export const getDeprecationLabel = function (c: Concept): string | null {
    if (!c) {
        return null;
    }
    var depLabel: Label | undefined = c.labels.find(l => l.roleName === "deprecatedLabel");
    if (depLabel) {
        return depLabel.value;
    }
    return null;
}

export const getTaxonomyConceptDetail = async function (accountId: string, year: number, dispatch: any) {

    // calling thunk to get the concept detail
    const response = await dispatch(loadTaxonomyConcept({ conceptId: accountId, afrYear: year }));
    const concept: Concept = unwrapResult(response);

    return concept;
}

export const loadTaxonomyConcept = createAsyncThunk(
    "Taxonomy/GetVersionedConceptDetails",
    async (conceptRef: ConceptRef): Promise<DtsConcept | null> => {
        try {
            var cref = conceptRef.conceptId + "^" + conceptRef.afrYear;
            var tt = conceptRef.taxonomyType;
            const result = await http<undefined, DtsConcept>({
                path: `/taxonomy/${tt}/versioned/${cref}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const loadReplacementConcept = createAsyncThunk(
    "Taxonomy/GetReplacementConceptDetails",
    async (conceptRef: ConceptRef): Promise<DtsConcept | null> => {
        try {
            var cref = conceptRef.conceptId + "^" + conceptRef.afrYear;
            var tt = conceptRef.taxonomyType;                                                                                                                                                                                                                                     
            const result = await http<undefined, DtsConcept>({
                path: `/taxonomy/${tt}/versioned/${cref}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });


export const getAccountId = function (rowid: string) {
    if (!rowid) {
        return "none";
    }
    var parts = rowid.split("|");
    if (parts.length > 1) {
        return "AccountCode_" + parts[1];
    }
    return parts[0];
}

export const taxonomyConceptSlice = createSlice({
    name: 'TaxonomyConcept',
    initialState,
    reducers: {
        resetTaxonomyConceptState: (state: any) => {
            return initialState
        },
        setSelectedTaxonomyConcept: (state: any, action: any) => {
            state.selectedConcept = action.payload;
        },
        setSelectedReplacementConcept: (state: any, action: any) => {
            state.selectedReplacementConcept = action.payload;
        },
        clearSelectedReplacementConcept: (state: any) => {
            state.selectedReplacementConcept = null;
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadTaxonomyConcept.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadTaxonomyConcept.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.selectedConcept = action.payload;
            })
            .addCase(loadReplacementConcept.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadReplacementConcept.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.selectedReplacementConcept = action.payload;
            })
    },
});

export const { resetTaxonomyConceptState, setSelectedTaxonomyConcept, setSelectedReplacementConcept, clearSelectedReplacementConcept } = taxonomyConceptSlice.actions;

export const getSelectedTaxonomyConcept = (state: RootState) => state.taxonomyConcept ? state.taxonomyConcept.selectedConcept : null;

export const getSelectedReplacementConcept = (state: RootState) => state.taxonomyConcept ? state.taxonomyConcept.selectedReplacementConcept : null;

export const getSelectedUnderlyingConcept = (state: RootState) => (state.taxonomyConcept && state.taxonomyConcept.selectedConcept) ? state.taxonomyConcept.selectedConcept.concept : null;

export default taxonomyConceptSlice.reducer;